import { DataValidationError } from "../Errors/DataValidationError.js";
import { InvalidArgumentError } from "../Errors/InvalidArgumentError.js";
import { SEARCH_API_MAX_LIMIT, SEARCH_TEXT_MAX_LENGTH } from "../Constants/MapboxSearch.js";

export class ValidationService {
  /**
   * Validate GeocodingClientOptions.
   * @param {Object} options Geocoding client options
   * @param {string} options.cdnBaseUrl Base URL of the CDN storing configuration data
   * @param {number} options.debouncePeriod Debounce period in milliseconds
   * @param {Object} options.gtfs GTFS API configuration object
   * @param {Object} options.limit Result limits configuration object
   * @param {Logger} options.logger Logger functions to use in the SDK
   * @param {Object} options.mapbox Mapbox API configuration object
   * @param {string} options.mapboxAccessToken Mapbox access token
   * @throws InvalidArgumentError if a given option is invalid
   */
  static validateGeocodingClientOptions({
    cdnBaseUrl,
    debouncePeriod,
    gtfs,
    limit,
    logger,
    mapbox,
    mapboxAccessToken,
  }) {
    const errorMessagePrefix = "GeocodingClient error:";

    ValidationService._validateLogger(logger);

    ValidationService._validateGtfsOptions(gtfs, errorMessagePrefix);
    ValidationService._validateMapboxOptions(mapbox, mapboxAccessToken, errorMessagePrefix);
    ValidationService._validateLimitOptions(limit, errorMessagePrefix);

    if (debouncePeriod != null && !ValidationService._positiveNumber(debouncePeriod)) {
      throw new InvalidArgumentError(`${errorMessagePrefix} debounce period must be a positive number`);
    }

    if (!ValidationService._nonEmptyString(cdnBaseUrl)) {
      throw new InvalidArgumentError(`${errorMessagePrefix} the cdn base url is required`);
    }
  }

  /**
   * Validate GTFS options.
   * @param {GtfsApiOptions} gtfs GTFS client configuration options
   * @param {string} errorMessagePrefix Prefix to apply to error messages
   * @throws InvalidArgumentError if a given option is invalid
   */
  static _validateGtfsOptions(gtfs, errorMessagePrefix) {
    if (
      !gtfs?.apiBaseUrl ||
      !ValidationService._nonEmptyString(gtfs.apiBaseUrl) ||
      !gtfs?.apiKey ||
      !ValidationService._nonEmptyString(gtfs.apiKey)
    ) {
      throw new InvalidArgumentError(`${errorMessagePrefix} gtfs api configuration is required`);
    }
    if (gtfs?.timeout != null) {
      ValidationService._validateTimeout(gtfs.timeout);
    }
  }

  /**
   * Validate Mapbox options.
   * @param {MapboxApiOptions} mapbox Mapbox client configuration options
   * @param {String} mapboxAccessToken Deprecated access token
   * @param {string} errorMessagePrefix Prefix to apply to error messages
   * @throws InvalidArgumentError if a given option is invalid
   */
  static _validateMapboxOptions(mapbox, mapboxAccessToken, errorMessagePrefix) {
    const tokenDefined = ValidationService._nonEmptyString(mapboxAccessToken);
    if (!tokenDefined && !ValidationService._nonEmptyString(mapbox?.accessToken)) {
      throw new InvalidArgumentError(`${errorMessagePrefix} mapbox access token is required`);
    }
    if (tokenDefined && ValidationService._nonEmptyString(mapbox?.accessToken)) {
      throw new InvalidArgumentError(
        `${errorMessagePrefix} mapbox access token defined using both deprecated and options forms`
      );
    }
    if (mapbox?.timeout != null) {
      ValidationService._validateTimeout(mapbox.timeout);
    }
  }

  /**
   * Validate ForwardGeocodeOptions.
   * @param {ForwardGeocodeOptions} options Forward geocode request options
   * @throws InvalidArgumentError if any of the options given are invalid
   */
  static validateForwardGeocodeOptions({ query, proximity, limit }) {
    const errorMessagePrefix = "Forward geocode error:";

    ValidationService._validateQueryString(query, errorMessagePrefix);
    if (proximity) {
      ValidationService._validateCoordinates(proximity, errorMessagePrefix, "proximity");
    }
    ValidationService._validateLimitOptions(limit, errorMessagePrefix);
  }

  /**
   * Validate ReverseGeocodeOptions.
   * @param {ReverseGeocodeOptions} options Reverse geocode request options
   * @throws InvalidArgumentError if any of the options given are invalid
   */
  static validateReverseGeocodeOptions({ coordinates }) {
    const errorMessagePrefix = "Reverse geocode error:";

    ValidationService._validateCoordinates(coordinates, errorMessagePrefix);
  }

  /**
   * Validate a category mapping JSON file data.
   * @param {string|Object} cdnData The content of the configuration file from CDN.
   * @throws DataValidationError if the given cdn data does not have the proper category mapping data
   */
  static validateCategoryMapping(cdnData) {
    const errorMessagePrefix = "Invalid category mapping JSON file from CDN:";

    if (!cdnData) {
      throw new DataValidationError(`${errorMessagePrefix} empty file`);
    }
    if (!ValidationService._validJSON(cdnData)) {
      throw new DataValidationError(`${errorMessagePrefix} invalid JSON file`);
    }

    let validSchema = true;
    if (Array.isArray(cdnData)) {
      validSchema = false;
    }
    if (
      !Object.prototype.hasOwnProperty.call(cdnData, "mapping") ||
      !Object.prototype.hasOwnProperty.call(cdnData, "priority")
    ) {
      validSchema = false;
    }
    if (typeof cdnData.mapping === "object") {
      for (const mappingKey of Object.keys(cdnData.mapping)) {
        if (
          !ValidationService._nonEmptyString(mappingKey) ||
          !ValidationService._arrayOfStrings(cdnData.mapping[mappingKey])
        ) {
          validSchema = false;
          break;
        }
      }
    } else {
      validSchema = false;
    }
    if (!ValidationService._arrayOfStrings(cdnData.priority)) {
      validSchema = false;
    }

    if (!validSchema) {
      throw new DataValidationError(`${errorMessagePrefix} invalid schema`);
    }
  }

  /**
   * Validate a postcode mapping JSON file data.
   * @param {string|Object} cdnData The content of the configuration file from CDN.
   * @throws DataValidationError if the given cdn data does not have the proper postcode mapping data
   */
  static validatePostcodeMapping(cdnData) {
    const errorMessagePrefix = "Invalid postcode mapping JSON file from CDN:";

    if (!cdnData) {
      throw new DataValidationError(`${errorMessagePrefix} empty file`);
    }
    if (!ValidationService._validJSON(cdnData)) {
      throw new DataValidationError(`${errorMessagePrefix} invalid JSON file`);
    }

    let validSchema = true;
    if (Array.isArray(cdnData)) validSchema = false;
    for (const value of Object.values(cdnData)) {
      if (typeof value !== "string") {
        validSchema = false;
        break;
      }
    }

    if (!validSchema) {
      throw new DataValidationError(`${errorMessagePrefix} invalid schema`);
    }
  }

  /**
   * Validate Timeout.
   * @param timeout Timeout value
   * @param [errorMessagePrefix] Error message prefix
   * @param [fieldName] Field name of the coordinates to use in the error message (default: "coordinates")
   * @throws InvalidArgumentError if the given Coordinates are invalid
   */
  static _validateTimeout(timeout, errorMessagePrefix, fieldName = "timeout") {
    const prefix = errorMessagePrefix ? `${errorMessagePrefix} ` : "";
    if (!ValidationService._positiveNumber(timeout)) {
      throw new InvalidArgumentError(`${prefix}${fieldName} must be a positive number`);
    }
  }

  /**
   * Validate LimitOptions.
   * @param {LimitOptions} [limit] Limit options
   * @param [errorMessagePrefix] Error message prefix
   * @throws InvalidArgumentError if the given LimitOptions are invalid
   */
  static _validateLimitOptions(limit, errorMessagePrefix) {
    const prefix = errorMessagePrefix ? `${errorMessagePrefix} ` : "";
    if (limit?.mapbox != null && !ValidationService._positiveNumber(limit.mapbox)) {
      throw new InvalidArgumentError(`${prefix}mapbox limit must be greater than or equal to 0`);
    }
    if (limit?.mapbox != null && limit?.mapbox > SEARCH_API_MAX_LIMIT) {
      throw new InvalidArgumentError(`${prefix}mapbox limit cannot exceed ${SEARCH_API_MAX_LIMIT}`);
    }
    if (limit?.gtfs) {
      const { gtfs } = limit;
      if (gtfs.station == null && gtfs.others == null && gtfs.total == null) {
        throw new InvalidArgumentError(`${prefix}GTFS limit object must not be empty`);
      }
      if (gtfs.station != null && !ValidationService._positiveNumber(gtfs.station)) {
        throw new InvalidArgumentError(`${prefix}GTFS station limit must be greater than or equal to 0`);
      }
      if (gtfs.others != null && !ValidationService._positiveNumber(gtfs.others)) {
        throw new InvalidArgumentError(`${prefix}GTFS others limit must be greater than or equal to 0`);
      }
      if (gtfs.total != null && !ValidationService._positiveNumber(gtfs.total)) {
        throw new InvalidArgumentError(`${prefix}GTFS total limit must be greater than or equal to 0`);
      }
    }
  }

  /**
   * Validate Logger.
   * @param {Object} logger Logger
   * @param {function} logger.error Error log function
   * @param {function} logger.info Info log function
   * @param {string} errorMessagePrefix Error message prefix
   * @throws InvalidArgumentError if the given Logger are invalid
   */
  static _validateLogger(logger, errorMessagePrefix) {
    const prefix = errorMessagePrefix ? `${errorMessagePrefix} ` : "";
    if (logger && typeof logger !== "object") {
      throw new InvalidArgumentError(`${prefix}logger object is invalid`);
    }
    if (logger?.error != null && !ValidationService._function(logger.error)) {
      throw new InvalidArgumentError(`${prefix}logger error function is invalid`);
    }
    if (logger?.info != null && !ValidationService._function(logger.info)) {
      throw new InvalidArgumentError(`${prefix}logger info function is invalid`);
    }
  }

  /**
   * Validate Coordinates.
   * @param {Coordinates} coordinates Coordinates
   * @param [errorMessagePrefix] Error message prefix
   * @param [fieldName] Field name of the coordinates to use in the error message (default: "coordinates")
   * @throws InvalidArgumentError if the given Coordinates are invalid
   */
  static _validateCoordinates(coordinates, errorMessagePrefix, fieldName = "coordinates") {
    const prefix = errorMessagePrefix ? `${errorMessagePrefix} ` : "";
    if (
      !Array.isArray(coordinates) ||
      coordinates.length !== 2 ||
      coordinates.some((element) => typeof element !== "number")
    ) {
      throw new InvalidArgumentError(
        `${prefix}${fieldName} must be a coordinates array of 2 numbers as [longitude, latitude]`
      );
    }
  }

  /**
   * Validate Query string for ForwardGeocode and ReverseGeocode.
   * @param [query] Query string
   * @param [errorMessagePrefix] Error message prefix
   * @param [fieldName] Field name of the query string to use in the error message (default: "query text")
   */
  static _validateQueryString(query, errorMessagePrefix, fieldName = "query text") {
    const prefix = errorMessagePrefix ? `${errorMessagePrefix} ` : "";
    if (!query || !ValidationService._nonEmptyString(query)) {
      throw new InvalidArgumentError(`${prefix}${fieldName} must be a non-empty string`);
    } else if (query.length > SEARCH_TEXT_MAX_LENGTH) {
      throw new InvalidArgumentError(`${prefix}${fieldName} must be no more than 120 characters`);
    }
  }

  /**
   * Validate provided value is a function.
   * @param {function} value value to check for being a function
   * @returns true if value is a function, false otherwise
   */
  static _function(value) {
    return typeof value === "function";
  }

  /**
   * Validate provided value is a number and positive (greater than or equal to zero).
   * @param {number} value value to check for being a number and positive
   * @returns true if value is a number and positive, false otherwise
   */
  static _positiveNumber(value) {
    return typeof value === "number" && value >= 0;
  }

  /**
   * Validate provided value is a string and non empty.
   * @param {String} value value to check for being a string and non empty
   * @returns true if value is a string and non empty, false otherwise
   */
  static _nonEmptyString(value) {
    return typeof value === "string" && value.length > 0;
  }

  /**
   * Validate if value is a JSON object or array.
   * @param {*} value value to check
   * @returns true if value is of type object or array, false otherwise
   */
  static _validJSON(value) {
    return typeof value === "object"; // also covers if value if type array
  }

  /**
   * Validate if value is an array of strings.
   * @param {*} value value to check
   * @returns true if value is an array of strings, false otherwise
   */
  static _arrayOfStrings(value) {
    if (!Array.isArray(value)) return false;
    for (const item of value) {
      if (typeof item !== "string") return false;
    }
    return true;
  }
}
